// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core';
import * as React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Button from 'react-bootstrap/lib/Button';
import Modal from 'react-bootstrap/lib/Modal';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import { Mutation, Query } from 'react-apollo';
import moment from 'moment';
import cuid from 'cuid';

import type { CommonActionProps } from './types.actions';
import type { GridItem, ActionStatus } from '../../types';

import ToggleController from '../../controllers/ToggleController';
import StatusController from '../../controllers/StatusController';
import ButtonAction from './ButtonAction';
import Row from '../Row';
import Col from '../Col';
import DescriptionField from '../DescriptionField';
import SingleField from '../SingleField';
import DatePickerField from '../DatePickerField';
import DropdownField from '../DropdownField';
import StatusMessage from '../StatusMessage';
import Prompt from '../Prompt';
import Loading from '../Loading';
import { createOrderMutation } from '../../graphql/mutations/orders/createOrder.mutation';
import getMenuItems from '../../graphql/queries/getMenuItems.queries';
import getActiveOrderQuery from '../../graphql/queries/getActiveOrder.query';
import createOrderContentQuery from '../../graphql/queries/createOrderContent.query';
import messages from '../../config/messages';
import { getDateFormat, removeTypeNamesFromOrderAccount } from '../../utils';
import { DesktopView, MobileView } from '../../config/mediaQuery';

type Props = {
  onOrderCreated: ActionStatus => typeof undefined,
  orderCreateStatus?: ActionStatus,
  onMutationCompleted: () => mixed,
  item: GridItem,
};

type State = {
  dateValue: moment | null,
  dirty: boolean,
  closing: boolean,
};

const CreateOrderSchema = Yup.object().shape({
  OrderType: Yup.string().required('Type is required'),
  Po: Yup.string().max(25, "PO # can't be longer than 25 chars"),
  Note: Yup.string().max(150, "Notes can't be longer than 150 chars"),
  OrderDate: Yup.string().required('Date is required'),
  Coop: Yup.string(),
});

class CreateOrderAction extends React.Component<
  CommonActionProps & Props,
  State,
> {
  static defaultProps = {
    size: 'xsmall',
    itemHasContent: false,
  };

  closingRequested: TimeoutID;
  closingSuccessed: TimeoutID;

  constructor(props: CommonActionProps & Props) {
    super(props);

    this.state = {
      dateValue: null,
      dirty: false,
      closing: false,
    };
  }

  onDateChange = (d: moment) => {
    this.setState({
      dateValue: d,
    });
  };

  onCreateCompleted = (options: {
    data: any,
    onToggleVisibility: () => mixed,
    resetForm: () => mixed,
  }) => {
    const { onToggleVisibility, resetForm } = options;

    this.props.onOrderCreated('completed');

    this.setState({
      closing: true,
    });

    // local clean up
    this.resetForm(false);

    if (this.closingRequested) {
      clearTimeout(this.closingRequested);
    }

    this.closingRequested = setTimeout(() => {
      this.onExit({
        onToggleVisibility,
        loading: false,
        dirty: false,
        resetForm,
        action: 'created',
      });
    }, 2000);
  };

  resetForm = (resetDate?: boolean) => {
    const d = resetDate
      ? {
          dateValue: null,
        }
      : {};

    this.setState({
      ...d,
      dirty: false,
    });

    if (this.closingSuccessed) {
      clearTimeout(this.closingSuccessed);
    }

    this.closingSuccessed = setTimeout(() => {
      this.setState({
        closing: false,
      });
    }, 500);
  };

  onExit = (options: {
    onToggleVisibility: () => mixed,
    dirty: boolean,
    loading: boolean,
    action: 'created' | 'canceled',
    resetForm?: () => mixed,
    force?: boolean,
    shouldResetData?: boolean,
  }) => {
    const {
      onToggleVisibility,
      dirty,
      force,
      action,
      resetForm,
      loading,
      shouldResetData = true,
    } = options;

    // if the mutation hasn't finished this will avoid closing the modal until
    // the transaction has been completed.
    if (loading) return;

    if (dirty && !force) {
      this.setState({
        dirty,
      });

      return;
    }

    onToggleVisibility();

    // formik's reset form
    if (typeof resetForm === 'function') {
      resetForm();
    }

    // local clean up
    this.resetForm(shouldResetData);

    if (action === 'created') {
      this.props.onMutationCompleted();
    }
  };

  componentWillUnmount() {
    if (this.closingRequested) {
      clearTimeout(this.closingRequested);
    }

    if (this.closingSuccessed) {
      clearTimeout(this.closingSuccessed);
    }
  }

  cancelConfirmPrompt = () => {
    this.setState({
      dirty: false,
    });
  };

  render() {
    return (
      <ToggleController>
        {({ onToggleVisibility, visible }) => (
          <Query
            query={createOrderContentQuery}
            variables={{
              selectedRow: removeTypeNamesFromOrderAccount(this.props.item),
            }}
            skip={!visible}
            notifyOnNetworkStatusChange
          >
            {(response: any) => {
              const { data, loading: contentLoading } = response || {};

              return (
                <Formik
                  initialValues={{
                    OrderType: contentLoading
                      ? ''
                      : data && data.createOrderContent.typeOptions[0].value,
                    OrderDate: contentLoading
                      ? ''
                      : data && data.createOrderContent.minDate,
                    Note: '',
                    Coop: '',
                    Po: '',
                  }}
                  isInitialValid={
                    contentLoading
                      ? false
                      : /**
                         * This is basically asking for the existance of OrderType
                         * and a OrderDate as minimal requirements to consider the
                         * form as valid
                         */
                        data &&
                        data.createOrderContent.typeOptions &&
                        data.createOrderContent.typeOptions[0] &&
                        data.createOrderContent.typeOptions[0].value &&
                        data.createOrderContent.minDate
                  }
                  enableReinitialize
                  validationSchema={CreateOrderSchema}
                >
                  {({
                    values,
                    errors,
                    touched,
                    isValid,
                    isInitialValid,
                    setFieldValue,
                    setFieldTouched,
                    resetForm,
                    dirty,
                  }) => (
                    <Mutation
                      mutation={createOrderMutation}
                      variables={{
                        input: {
                          ...values,
                          Account: this.props.item,
                        },
                      }}
                      onCompleted={dataMutation =>
                        this.onCreateCompleted({
                          data: dataMutation,
                          onToggleVisibility,
                          resetForm,
                        })
                      }
                      refetchQueries={() => {
                        return [
                          {
                            query: getMenuItems,
                          },
                          {
                            query: getActiveOrderQuery,
                          },
                        ];
                      }}
                      skip={!visible}
                    >
                      {(mutation, { loading, error }) => {
                        return (
                          <StatusController
                            hasError={!!error}
                            loading={loading}
                          >
                            {({ status }) => {
                              const isOrderCreated =
                                status === 'completed' ||
                                this.props.orderCreateStatus === 'completed' ||
                                this.state.closing;

                              return (
                                <React.Fragment>
                                  <DesktopView>
                                    <ButtonAction
                                      bsSize={this.props.size}
                                      disabled={this.props.disabled}
                                      onClick={onToggleVisibility}
                                    >
                                      <Glyphicon glyph="plus" />{' '}
                                      <span>
                                        {(status === '' || status === 'error') &&
                                          'Create Order'}
                                        {status === 'loading' &&
                                          'Creating order...'}
                                        {status === 'completed' &&
                                          'Order Created!'}
                                      </span>
                                    </ButtonAction>
                                  </DesktopView>
                                  <MobileView>
                                    <button
                                      disabled={this.props.disabled || status !== ""}
                                      onClick={onToggleVisibility}
                                    >
                                      <img src='assets/plus.svg' alt='create icon'/>
                                      {(status === '' || status === 'error') &&
                                        'Create Order'}
                                      {status === 'loading' &&
                                        'Creating order...'}
                                      {status === 'completed' &&
                                        'Order Created!'}
                                    </button>
                                  </MobileView>
                                  {visible && (
                                    <MobileView>
                                      {(isMobile) => {
                                        return (
                                          <Modal
                                            show={visible}
                                            onHide={() =>
                                              this.onExit({
                                                onToggleVisibility,
                                                dirty,
                                                resetForm,
                                                loading,
                                                action: 'canceled',
                                              })
                                            }
                                            className="modal--form"
                                            dialogClassName={isMobile ? 'create-order-modal': ''}
                                          >
                                            <Modal.Header closeButton={!isMobile}>
                                              <Modal.Title>
                                                Create Order for{' '}
                                                {this.props.item.AcctName}
                                              </Modal.Title>
                                            </Modal.Header>

                                            {contentLoading ? (
                                              <Modal.Body>
                                                <Loading />
                                              </Modal.Body>
                                            ) : (
                                              <DesktopView>
                                                {() => {
                                                  const orderType = <DropdownField
                                                    value={values.OrderType}
                                                    options={
                                                      data &&
                                                      data.createOrderContent
                                                        .typeOptions
                                                    }
                                                    id={`type-${cuid()}`}
                                                    title="Type"
                                                    name="OrderType"
                                                    setFieldValue={setFieldValue}
                                                    setFieldTouched={setFieldTouched}
                                                    errors={errors}
                                                    touched={touched}
                                                    disabled={isOrderCreated}
                                                    mobileModal={isMobile}
                                                  />;
                                                  const datePicker = <DatePickerField
                                                    label="Date"
                                                    onChange={this.onDateChange}
                                                    value={
                                                      this.state.dateValue ||
                                                      moment(
                                                        values.OrderDate,
                                                        getDateFormat('server'),
                                                      )
                                                    }
                                                    name="OrderDate"
                                                    setFieldValue={setFieldValue}
                                                    setFieldTouched={
                                                      setFieldTouched
                                                    }
                                                    errors={errors}
                                                    touched={touched}
                                                    disabled={isOrderCreated}
                                                    holidays={
                                                      data &&
                                                      data.createOrderContent
                                                        .disabledDates.holidays
                                                    }
                                                    disabledDays={
                                                      data &&
                                                      data.createOrderContent
                                                        .disabledDates.days
                                                    }
                                                    minSelectableDay={
                                                      data &&
                                                      data.createOrderContent
                                                        .minDate
                                                    }
                                                    maxSelectableDay={
                                                      data &&
                                                      data.createOrderContent
                                                        .maxDate
                                                    }
                                                    mobileModal={isMobile}
                                                  />;
                                                  const notes = <DescriptionField
                                                    label="Notes"
                                                    name="Note"
                                                    placeholder=""
                                                    errors={errors}
                                                    touched={touched}
                                                    value={values.Note}
                                                    readOnly={false}
                                                    inline
                                                    fieldProps={{
                                                      disabled: isOrderCreated,
                                                    }}
                                                  />;
                                                  const coops = <DropdownField
                                                    value={values.Coop}
                                                    options={
                                                      data &&
                                                      data.createOrderContent &&
                                                      data.createOrderContent.coopOptions
                                                    }
                                                    id={`coop-${cuid()}`}
                                                    title="COOP"
                                                    name="Coop"
                                                    setFieldValue={setFieldValue}
                                                    setFieldTouched={
                                                      setFieldTouched
                                                    }
                                                    errors={errors}
                                                    touched={touched}
                                                    disabled={isOrderCreated}
                                                    mobileModal={isMobile}
                                                  />;
                                                  const poNum = <SingleField
                                                    label="PO #"
                                                    name="Po"
                                                    placeholder=""
                                                    errors={errors}
                                                    touched={touched}
                                                    value={values.Po}
                                                    readOnly={false}
                                                    fieldProps={{
                                                      disabled: isOrderCreated,
                                                    }}
                                                    inline={isMobile}
                                                  />;
                                                  return (
                                                    <Form>
                                                      <Modal.Body>
                                                        {loading && <Loading />}
                                                        {this.state.dirty && (
                                                          <Prompt
                                                            onCancel={
                                                              this.cancelConfirmPrompt
                                                            }
                                                            onConfirm={() =>
                                                              this.onExit({
                                                                onToggleVisibility,
                                                                resetForm,
                                                                loading,
                                                                dirty: false,
                                                                force: true,
                                                                action: 'canceled',
                                                              })
                                                            }
                                                            message={
                                                              messages.common.unsaved
                                                            }
                                                          />
                                                        )}
                                                        <DesktopView>
                                                          {orderType}
                                                          {data &&
                                                            data.createOrderContent.orderTypesWithDatePicker.includes(
                                                              values.OrderType,
                                                            ) && (
                                                              datePicker
                                                            )}
                                                          {notes}
                                                          {data &&
                                                            data.createOrderContent
                                                              .coopOptions &&
                                                            data.createOrderContent
                                                              .coopOptions.length > 1 && coops
                                                          }
                                                          {poNum}
                                                        </DesktopView>
                                                        <MobileView>
                                                          <table style={{ width: '100%' }}>
                                                            <tbody>
                                                              <tr>
                                                                <td align="left" style={{ width: '50%' }}>{orderType}</td>
                                                                <td align="left" style={{ width: '50%' }}>{data &&
                                                                  data.createOrderContent
                                                                    .coopOptions &&
                                                                  data.createOrderContent
                                                                    .coopOptions.length > 1 ? coops : ' '
                                                                }</td>
                                                              </tr>
                                                            </tbody>
                                                          </table>
                                                          {data &&
                                                            data.createOrderContent.orderTypesWithDatePicker.includes(
                                                              values.OrderType,
                                                            ) && (
                                                              datePicker
                                                            )}
                                                          {notes}
                                                          {poNum}
                                                        </MobileView>
                                                      </Modal.Body>
                                                      <Modal.Footer>
                                                        <Row
                                                          css={{
                                                            alignItems: 'center',
                                                          }}
                                                        >
                                                          <Col>
                                                            {(status === 'completed' ||
                                                              status === '') && (
                                                                <StatusMessage
                                                                  status={status}
                                                                >
                                                                  Order successfully created!
                                                                </StatusMessage>
                                                              )}
                                                            {(status === 'error' ||
                                                              status === '') && (
                                                                <StatusMessage
                                                                  status={status}
                                                                >
                                                                  There has been an error
                                                                  while trying to create the
                                                                  order.
                                                                </StatusMessage>
                                                              )}
                                                          </Col>
                                                          <DesktopView>
                                                            <Col
                                                              css={{
                                                                marginLeft: 'auto',
                                                              }}
                                                            >
                                                              <Button
                                                                disabled={
                                                                  status !== '' ||
                                                                  (!isInitialValid &&
                                                                    !isValid) ||
                                                                  isOrderCreated
                                                                }
                                                                onClick={mutation}
                                                              >
                                                                {isOrderCreated &&
                                                                  'Order Created!'}
                                                                {(status === '' ||
                                                                  status === 'error') &&
                                                                  !isOrderCreated &&
                                                                  'Create Order'}
                                                                {status === 'loading' &&
                                                                  'Creating order...'}
                                                              </Button>
                                                            </Col>
                                                          </DesktopView>
                                                          <MobileView>
                                                            {(!isOrderCreated) &&
                                                              <div className='columns-bottom-button-group'>
                                                                <button
                                                                  onClick={onToggleVisibility}
                                                                >
                                                                  Cancel
                                                                </button>
                                                                <button
                                                                  disabled={
                                                                    status !== '' ||
                                                                    (!isInitialValid &&
                                                                      !isValid) ||
                                                                    isOrderCreated
                                                                  }
                                                                  onClick={mutation}
                                                                >
                                                                  {(status === '' ||
                                                                    status === 'error') &&
                                                                    !isOrderCreated &&
                                                                    'Create Order'}
                                                                  {status === 'loading' &&
                                                                    'Creating order...'}
                                                                </button>
                                                              </div>
                                                            }
                                                          </MobileView>
                                                        </Row>
                                                      </Modal.Footer>
                                                    </Form>

                                                  )
                                                }}

                                              </DesktopView>
                                            )}
                                          </Modal>
                                        )
                                      }}
                                    </MobileView>
                                  )}
                                </React.Fragment>
                              );
                            }}
                          </StatusController>
                        );
                      }}
                    </Mutation>
                  )}
                </Formik>
              );
            }}
          </Query>
        )}
      </ToggleController>
    );
  }
}

export default CreateOrderAction;
